import React, { type ReactNode } from "react";
import { SimpleGrid } from "@chakra-ui/react";
import { Section } from "components/section";
import { TextColumn } from "ui/text";
import {
  CodeIcon,
  ExperimentsIcon,
  HeartIcon,
  LeversIcon,
  MegaphoneIcon,
  MinusCircleIcon,
  PersonIcon,
  SparklesIcon,
  TrendIcon,
} from "ui/icons";
import { FilterTabs } from "ui/tabs";
import { Rule } from "ui/rule";

function TabSection({ children }: { children: ReactNode }) {
  return (
    <SimpleGrid columns={{ base: 1, md: 3 }} gap={{ base: "biggest", md: "small" }}>
      {children}
    </SimpleGrid>
  );
}

function FirstTabSection() {
  return (
    <TabSection>
      <TextColumn
        iconChip={{ variant: "red", icon: HeartIcon }}
        title="Retain users with data-driven loyalty programs"
        description="Use data to create loyalty programs that work. Power the best offers and most relevant messages to every customer."
        cta={{ href: "/customers/petsmart" }}
      />
      <TextColumn
        iconChip={{ variant: "yellow", icon: TrendIcon }}
        title="Increase LTV with 1-1 personalized product offers"
        description="Always recommend the next-best-action by leveraging all of your organization’s data including data science and product catalogs."
        cta={{ href: "/customers/wmg" }}
      />
      <TextColumn
        iconChip={{ variant: "violet", icon: PersonIcon }}
        title="Drive higher engagement with omni-channel personalization"
        description="Make sure customers are receiving the right message on the right channel at the right time by centrally managing your audience logic."
        cta={{ href: "/playbooks/Product-Utilization-Emails-Iterable" }}
      />
    </TabSection>
  );
}

function SecondTabSection() {
  return (
    <TabSection>
      <TextColumn
        iconChip={{ variant: "violet", icon: MinusCircleIcon }}
        title="Reduce wasted spend with better suppression"
        description="Build custom suppression audiences and actively sync them into advertising platforms to optimize performance."
        cta={{ href: "/playbooks/Suppress-Existing-Customers-from-Facebook-Campaigns" }}
      />
      <TextColumn
        iconChip={{ variant: "green", icon: CodeIcon }}
        title="Increase ROAS by switching from pixels to conversion APIs"
        description="Enrich conversion events with first-party data through conversion APIs for higher user match rates and increased targeting accuracy."
        cta={{ href: "/playbooks/send-better-signals-with-facebook-conversion-api" }}
      />
      <TextColumn
        iconChip={{ variant: "yellow", icon: MegaphoneIcon }}
        title="Re-engage churned customers with hyper-targeted remarketing"
        description="Unlock highly personalized triggered campaigns like 1-1 abandon cart emails, reminders, and product recommendations."
        cta={{ href: "/playbooks/improve-conversions-with-personalized-funnel-based-ad-retargeting" }}
      />
    </TabSection>
  );
}

function ThirdTabSection() {
  return (
    <TabSection>
      <TextColumn
        iconChip={{ variant: "blue", icon: LeversIcon }}
        title="Understand the true lift of every channel with holdout testing"
        description="Understand incrementally of email and advertising programs with audience level hold-out testing"
        cta={{ href: "/platform/customer-studio#journeys" }}
      />
      <TextColumn
        iconChip={{ variant: "yellow", icon: ExperimentsIcon }}
        title="Drive higher ROI with omni-channel experimentation"
        description="Unlock audience-level testing across multiple channels. Simply measure user-level ROI across multiple treatments."
        cta={{ href: "/docs/customer-studio/splits", target: "_blank" }}
      />
      <TextColumn
        iconChip={{ variant: "violet", icon: SparklesIcon }}
        title="Close the loop with self-serve campaign analytics "
        description="Measure, explore, and analyze your campaign performance, all on your own. Take action on your insights with AB experiments and incrementally tests."
        cta={{ href: "/platform/campaign-intelligence" }}
      />
    </TabSection>
  );
}

export function UseCaseTabs() {
  return (
    <Section mt={{ base: "huge", md: "huger" }}>
      <Rule />
      <FilterTabs
        mt="bigger"
        justify="end"
        leftJustifiedTitle="Solve real problems, fast"
        tabs={[
          {
            label: "Lifecycle Marketing",
            items: <FirstTabSection />,
          },
          {
            label: "Paid Marketing",
            items: <SecondTabSection />,
          },
          {
            label: "Experimentation",
            items: <ThirdTabSection />,
          },
        ]}
      />
    </Section>
  );
}
